import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import CTA from 'Src/components/buttons/CTA';
import { Flex } from 'Src/components/layout/Flex';
import { useCheck } from 'Src/feature/check/useCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useCheckPagePayment } from 'Src/feature/payment/useCheckPagePayment';
import { usePaymentFlow } from 'Src/feature/payment/usePaymentFlow';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import useRoute from 'Src/routes/useRoute';

import { PaymentMethod } from './paymentMethodService';
import { useCheckPagePaymentPermissions } from './useCheckPagePaymentPermissions';

const sectionId = 'OtherPaymentModal';

interface FormModel {
  amount: string;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

interface OtherPaymentModalProps {
  control: ReturnType<typeof useCheckPagePayment>['modal']['otherPayment'];
}

const TEST_ID = {
  root: `OtherPaymentModal`,
  cta: {
    cancel: `OtherPaymentModal-cancel`,
    next: `OtherPaymentModal-next`,
    option: (label: string) => `OtherPaymentModal-option-${label}`,
  }
};

const OtherPaymentModalContent: FC<OtherPaymentModalProps> = ({ control }) => {
  const { t } = useTranslation();
  const { route } = useRoute();
  const log = useLogger().getSubLogger({ name: sectionId });

  const grantedPermissions = useCheckPagePaymentPermissions();
  const paymentFlow = usePaymentFlow();
  const checkId = route.params.active.checkId ? parseInt(route.params.active.checkId, 10) : undefined;
  const checkGetOne = useCheck.query.getOne(checkId);
  const otherPaymentMethods = usePaymentMethod.query.otherMethods();
  const check = checkGetOne.data;

  const [selectedPayMethod, setSelectedPayMethod] = useState<PaymentMethod | undefined>(undefined);
  const [isAmountInput, setIsAmountInput] = useState(false);

  const schema = useMemo(() => {
    // balance should never be undefined, but default to 1000 just in case and to make typescript happy
    let maxAmount = check?.balance.unpaid ?? 1000;
    if (paymentFlow.is.part) maxAmount = paymentFlow.part.amount;
    if (paymentFlow.is.split) maxAmount = paymentFlow.split.amount;
    return object().shape({
      // impose a maximum only when selectedPaymentMethod.type === 'Other', i.e. does not allow overpayment/change
      amount: selectedPayMethod?.type === 'Other' 
        ? number().label(t('labels.amount'))
          // using a string value for initializing amount as value should be "String.toFixed(2)"
          .transform((value) => parseFloat(value) || 0)
          .required()
          .moreThan(0)
          .max(maxAmount)
        : number().label(t('labels.amount'))
          // using a string value for initializing amount as value should be "String.toFixed(2)"
          .transform((value) => parseFloat(value) || 0)
          .required()
          .moreThan(0)
    });
  }, [check?.balance.unpaid, paymentFlow, t, selectedPayMethod]);

  const form = useForm<FormModel>({
    defaultValues: { amount: undefined },
    schema,
  });

  const subtitle = paymentFlow.basis === undefined
    ? t('payment.labels.amountPaidVsDue', { paid: check?.balance.paid ?? 0, due: check?.balance.unpaid ?? 0 })
    : t('payment.labels.cashBasis', { amount: paymentFlow.basis });

  const onPaymentMethodClick = (payMethod: PaymentMethod) => {
    setSelectedPayMethod(payMethod);
    setIsAmountInput(true);
  };

  const handleClose = () => {
    setSelectedPayMethod(undefined);
    setIsAmountInput(false);
    control.close();
  };

  const onNext = () => {
    if (typeof selectedPayMethod?.id !== 'number') return log.debug();
    control.onNext({
      // NOTE: the formatted "currencyInput" has difficulty returning number values, for edge cases where the stored value is a string, normalize output value from "currencyInput"
      amount: parseFloat(form.action.getValues().amount.toString()),
      paymentMethodId: selectedPayMethod.id,
    });
    handleClose();
  };

  return isAmountInput
    ? (
      <>
        <ModalHeader>
          <ModalTitle copy={t('otherPaymentOptions.labels.enterPaymentValue')} />
        </ModalHeader>
        <ModalContent flex={{ alignItems: 'center', justifyContent: 'center', gap: 'md' }}>
          <Form usedForm={form} omitChangeTracking>
            <Field type="currencyInput" name="amount" subtitle={subtitle} />
          </Form>
        </ModalContent>
        <ModalActions>
          <ModalAction data-testid={TEST_ID.cta.cancel} color="secondary" label={t('labels.cancel')} onClick={handleClose} />
          <ModalAction
            data-testid={TEST_ID.cta.next}
            color="primary"
            label={t('labels.next')}
            onClick={onNext}
            disabled={!form.state.isValid}
          />
        </ModalActions>
      </>
    ) : (
      <>
        <ModalHeader>
          <ModalTitle copy={t('otherPaymentOptions.label')} />
        </ModalHeader>
        <ModalContent flex={{ alignItems: 'center', justifyContent: 'center' }}>
          <Flex gap="sm">
            {(otherPaymentMethods.data || [])
              .filter((pm) => grantedPermissions.otherPayment.paymentMethodIds.includes(pm.id))
              .map((pm) => (
                <CTA
                  data-testid={TEST_ID.cta.option(pm.name)}
                  key={pm.id}
                  variant="outlined"
                  minWidth="md"
                  label={pm.name}
                  onClick={() => onPaymentMethodClick(pm)}
                />
              ))}
          </Flex>
        </ModalContent>
        <ModalActions>
          <ModalAction data-testid={TEST_ID.cta.cancel} color="secondary" label={t('labels.cancel')} onClick={handleClose} />
        </ModalActions>
      </>
    );
};

// eslint-disable-next-line react/no-multi-comp
const OtherPaymentModal: FC<OtherPaymentModalProps> = ({ control }) => (
  <Modal
    data-testid={TEST_ID.root}
    open={control.isOpen}
    onBackDropClick={control.close}
  >
    <OtherPaymentModalContent control={control} />
  </Modal>
);

export { OtherPaymentModal, TEST_ID };
export type { OtherPaymentModalProps };
